import React from 'react';
import { Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useAssessmentsKeysQuery, useAssessmentSubmissionsQuery } from '@uplift/queries';
import { AssessmentKeys } from '@uplift/types';
import { Chip, Loader } from '@uplift/ui-v2';
import { CalendarDateService } from '@uplift/services';
import { FormsChipFilter, SourceChipFilter, SourceChipFilterValue } from './ChipFilters';
import { AssessmentSubmission } from '@uplift/api';
import { mapAssessmentScoreToDescription } from '@uplift/utils';
import { useL10n } from '@uplift/hooks';


const AssessmentHistoryRow: React.VFC<{ assessmentKey: AssessmentKeys; assessmentSubmission: AssessmentSubmission }> = ({ assessmentKey, assessmentSubmission }) => {
  const l10n = useL10n();
  const dateCell = React.useMemo(() => {
    if (assessmentSubmission.submission.completedAt) {
      return <Typography>{CalendarDateService.date(assessmentSubmission.submission.completedAt).format('MMM D')}</Typography>;
    }
    if (assessmentSubmission.submission.expiredAt) {
      return <Typography variant="body2" color="error">Expired {CalendarDateService.date(assessmentSubmission.submission.expiredAt).format('MMM D')}</Typography>;
    }

    return <Typography color="textDisabled">Not completed</Typography>;
  }, [ assessmentSubmission ]);

  return (
    <TableRow>
      <TableCell>
        {dateCell}
      </TableCell>
      <TableCell><Typography>{assessmentSubmission.score}</Typography></TableCell>
      <TableCell>{mapAssessmentScoreToDescription(assessmentKey, l10n)?.(assessmentSubmission.score) ?? '-'}</TableCell>
      <TableCell>{assessmentSubmission.submission.userId === assessmentSubmission.submission.processedBy
        ? <Chip label="Patient-completed" size="small" variant="outlined" color="primary" />
        : <Chip label="Clinician-administered" size="small" variant="outlined" color="primary" />
      }</TableCell>
    </TableRow>
  );
};

const AssessmentsHistoryTable: React.VFC<{ patientId: string; assessmentKey: AssessmentKeys; filter?: SourceChipFilterValue }> = (props) => {
  const { data: _submissions } = useAssessmentSubmissionsQuery(props.patientId, props.assessmentKey, true);

  const submissions = React.useMemo(() => {
    if (props.filter === 'patient') {
      return _submissions.filter(s => s.submission.userId === s.submission.processedBy);
    }
    if (props.filter === 'administer') {
      return _submissions.filter(s => s.submission.userId !== s.submission.processedBy);
    }

    return _submissions;
  }, [ props.filter, _submissions ]);

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ minWidth: '54px', width: '54px' }}>DATE</TableCell>
          <TableCell>SCORE</TableCell>
          <TableCell sx={{ width: '100%' }}>BAND</TableCell>
          <TableCell sx={{ width: '100%' }}>SOURCE</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {submissions.length ? submissions.map(submission => (
          <AssessmentHistoryRow
            key={submission.submission.formSubmissionId}
            assessmentKey={props.assessmentKey}
            assessmentSubmission={submission}
          />
        )) : (
          <TableRow>
            <TableCell colSpan={4}>
              <Typography color="textDisabled" variant="h5" textAlign="center" sx={{ p: 3 }}>
                No completed assessments
              </Typography>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};


export const HistoryTab: React.VFC<{ patientId: string }> = (props) => {
  const { data: assessmentKeys } = useAssessmentsKeysQuery();
  const [ assessmentKey, setAssessmentKey ] = React.useState(Object.keys(assessmentKeys)[0]);
  const [ completedByFilter, setCompletedByFilter ] = React.useState<SourceChipFilterValue>();

  return (
    <Stack gap={1} sx={{ p: 2 }}>
      <FormsChipFilter value={assessmentKey} setValue={setAssessmentKey} />
      <SourceChipFilter value={completedByFilter} setValue={setCompletedByFilter} />

      <React.Suspense fallback={<Loader height="300px" color="primary" />}>
        <AssessmentsHistoryTable
          patientId={props.patientId}
          assessmentKey={assessmentKey as AssessmentKeys}
          filter={completedByFilter}
        />
      </React.Suspense>
    </Stack>
  );
};
